<template>
  <div>
    <p>{{ input.label }}</p>
    <p>{{ input.description }}</p>
    <md-tabs
      :md-active-tab="selectedScope"
    >
      <md-tab
        v-for="scope in scopes"
        :id="scope"
        :key="`${input.id}-${scope}`"
        :md-label="scope"
        @click="changeScope(scope)"
      />
    </md-tabs>
    <div v-if="showIdentifierToolbar" class="identifier-toolbar">
      <md-tabs
        v-if="showTagsInput"
        :key="`${selectedScope}-${identifiersForScope.join('-')}`"
        :md-active-tab="selectedIdentifier"
        class="identifier-tabs"
      >
        <md-tab
          v-for="identifier in identifiersForScope"
          :id="identifier"
          :key="identifier"
          :md-label="'ID ' + identifier"
          @click="changeIdentifier(identifier)"
        />
      </md-tabs>
      <div
        v-else
        class="identifier-empty-state"
      >
        No IDs available. Press the + button to add a new ID.
      </div>
      <div v-if="!disabled" class="identifier-actions">
        <md-button
          class="md-icon-button md-dense"
          @click="openAddModal"
        >
          <md-icon>add</md-icon>
          <md-tooltip md-direction="top">
            Add new {{ selectedScope }} ID
          </md-tooltip>
        </md-button>
        <md-button
          v-if="selectedIdentifier"
          class="md-icon-button md-dense"
          @click="openEditModal(selectedIdentifier)"
        >
          <md-icon>edit</md-icon>
          <md-tooltip md-direction="top">
            Edit ID {{ selectedIdentifier }}
          </md-tooltip>
        </md-button>
        <md-button
          v-if="selectedIdentifier"
          class="md-icon-button md-dense"
          @click="openDeleteConfirmationModal(selectedIdentifier)"
        >
          <img src="@/assets/delete-button.svg" class="c-social-share__delete-img" alt="">
          <md-tooltip md-direction="top">
            Delete ID {{ selectedIdentifier }}
          </md-tooltip>
        </md-button>
      </div>
    </div>
    <meta-tags-per-language-input
      v-if="showTagsInput"
      :key="`${selectedScope}-${selectedIdentifier}`"
      :languages="languages"
      :disabled="disabled"
      :input-value="metaTagsForScope || {}"
      @update="handleUpdate($event)"
    />
  </div>
</template>

<script>
import { get } from 'lodash';
import { META_TAG_SCOPE_ROOT, META_TAG_SCOPES, isScopeWithoutIdentifiers } from '@/const/meta-tags';
import MetaTagsPerLanguageInput from '../meta-tags/MetaTagsPerLanguageInput.vue';
import AddOrUpdateScopeIdentifier from '../meta-tags/AddOrUpdateScopeIdentifier.vue';
import ConfirmationModalComponent from '../confirmation-modal/ConfirmationModalComponent.vue';

export default {
  name: 'SocialShareMetaTagsFormField',
  components: { MetaTagsPerLanguageInput },

  props: {
    input: {
      type: Object,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
  },

  data() {
    return {
      inputValue: {},
      scopes: META_TAG_SCOPES,
      selectedScope: META_TAG_SCOPE_ROOT,
      selectedIdentifier: '',
      selectedLanguage: this.languages[0],
    }
  },

  computed: {
    defaultInputValue() {
      try {
        return JSON.parse(get(this.input, 'defaultValue', {}))
      } catch (e) {
        return {}
      }
    },
    metaTagsForScope() {
      if (isScopeWithoutIdentifiers(this.selectedScope)) {
        return get(this.inputValue, this.selectedScope);
      } else {
        return get(this.inputValue, `${this.selectedScope}.${this.selectedIdentifier}`);
      }
    },
    identifiersForScope() {
      if (isScopeWithoutIdentifiers(this.selectedScope)) {
        return [];
      } else {
        return Object.keys(get(this.inputValue, this.selectedScope, {}));
      }
    },
    showTagsInput() {
      return isScopeWithoutIdentifiers(this.selectedScope) || this.identifiersForScope.length !== 0;
    },
    showIdentifierToolbar() {
      return !isScopeWithoutIdentifiers(this.selectedScope);
    }
  },

  created() {
    const inputValues = get(this.input, 'value', this.defaultInputValue)
    this.inputValue = inputValues || {}
  },

  methods: {
    changeScope(scope) {
      this.selectedScope = scope;
      if (isScopeWithoutIdentifiers(this.selectedScope)) {
        this.selectedIdentifier = '';
      } else {
        const identifiers = Object.keys(get(this.inputValue, this.selectedScope, {}));
        this.selectedIdentifier = identifiers.length > 0 ? identifiers[0] : '';
      }
    },

    changeIdentifier(identifier) {
      this.selectedIdentifier = identifier;
    },

    handleUpdate(value) {
      if (isScopeWithoutIdentifiers(this.selectedScope)) {
        this.inputValue = { ...this.inputValue, [this.selectedScope]: value };
      } else {
        this.inputValue = {
          ...this.inputValue,
          [this.selectedScope]: {
            ...this.inputValue[this.selectedScope],
            [this.selectedIdentifier]: value
          }
        };
      }
      this.$emit('update', {
        value: JSON.stringify(this.inputValue),
      });
    },

    openAddModal() {
      this.$modal.show(
        AddOrUpdateScopeIdentifier,
        {
          item: {
            identifier: '',
          },
          existingIdentifiers: this.identifiersForScope,
          successFunction: this.addIdentifier,
        },
        { height: 'auto', scrollable: true }
      );
    },

    openEditModal(identifier) {
      this.$modal.show(
        AddOrUpdateScopeIdentifier,
        {
          item: {
            identifier,
          },
          existingIdentifiers: this.identifiersForScope.filter((id) => id !== identifier),
          successFunction: (item) => this.editIdentifier(identifier, item),
        },
        { height: 'auto', scrollable: true }
      );
    },

    openDeleteConfirmationModal(identifier) {
      this.$modal.show(
        ConfirmationModalComponent,
        {
          confirmationMsg: `Are you sure you want to delete ${this.selectedScope} ID ${identifier}?`,
          onAccept: () => this.deleteIdentifier(identifier),
          onClose: () => {},
        },
        { height: 'auto', scrollable: false, width: "440px" }
      );
    },

    addIdentifier(item) {
      this.$set(this.inputValue, this.selectedScope, {
        ...this.inputValue[this.selectedScope],
        [item.identifier]: {},
      });
      this.selectedIdentifier = item.identifier;
      this.$emit('update', {
        value: JSON.stringify(this.inputValue),
      });
    },

    editIdentifier(oldIdentifier, item) {
      const newIdentifier = item.identifier;
      if (newIdentifier === oldIdentifier) {
        return;
      }
      const scope = get(this.inputValue, this.selectedScope, {});
      const updatedScope = {};
      Object.keys(scope).forEach((key) => {
        if (key === oldIdentifier) {
          updatedScope[newIdentifier] = JSON.parse(JSON.stringify(scope[oldIdentifier]));
        } else {
          updatedScope[key] = scope[key];
        }
      });
      this.$set(this.inputValue, this.selectedScope, updatedScope);
      this.selectedIdentifier = newIdentifier;
      this.$emit('update', {
        value: JSON.stringify(this.inputValue),
      });
    },

    deleteIdentifier(identifier) {
      const scope = get(this.inputValue, this.selectedScope, {});
      this.$delete(scope, identifier);
      const identifiers = Object.keys(scope);
      this.selectedIdentifier = identifiers.length > 0 ? identifiers[0] : '';
      this.$emit('update', {
        value: JSON.stringify(this.inputValue),
      });
    },
  },
}

</script>
<style lang="scss" scoped>
// same delete icon as every other table/list (src/assets/delete-button.svg)
.c-social-share__delete-img {
  display: block;
  flex-shrink: 0;
  width: 20px;
  min-width: 20px;
  height: 20px;
}

// field title + description, same as the other configuration fields
p {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 13px;
  color: var(--text-secondary);

  &:first-child {
    margin-bottom: 2px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  & + p + .md-tabs,
  & + p + * {
    margin-top: 12px;
  }
}

.identifier-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
}

.identifier-tabs {
  flex: 1;
  min-width: 0;

  ::v-deep .md-button {
    text-transform: none !important;
  }
}

.identifier-empty-state {
  flex: 1;
  min-width: 0;
  color: var(--text-muted);
  font-size: 14px;
}

.identifier-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
}
</style>
